import React, { useEffect, useState, useCallback } from 'react';
import './MisProyectosConDocs.css';
import { apiFetch } from '../../../services/dashboardApi';

const API_URL = 'http://localhost:8000/api';

function MisProyectosConDocs({ token }) {
    // Estados para proyectos creados
    const [proyectosCreados, setProyectosCreados] = useState([]);
    const [proyectoCreadoExpandido, setProyectoCreadoExpandido] = useState(null);
    const [docsCreados, setDocsCreados] = useState([]);

    // Estados para proyectos asignados
    const [asignaciones, setAsignaciones] = useState([]);
    const [proyectoExpandido, setProyectoExpandido] = useState(null);
    const [documentos, setDocumentos] = useState([]);

    // Estados comunes
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Estados para subir documentos
    const [archivo, setArchivo] = useState(null);
    const [descripcion, setDescripcion] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);

    // Estado para filtrar rechazados
    const [ocultarRechazados, setOcultarRechazados] = useState(false);

    const fetchMisProyectos = useCallback(async () => {
        try {
            // Cargar proyectos creados
            const responseCreados = await apiFetch(`${API_URL}/proyectos/mis-proyectos-creados/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (responseCreados.ok) {
                const dataCreados = await responseCreados.json();
                setProyectosCreados(dataCreados.proyectos || []);
            }

            // Cargar proyectos asignados
            const response = await apiFetch(`${API_URL}/proyectos/mis-proyectos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.status === 401) {
                setError('No autorizado. Inicia sesión nuevamente.');
                return;
            }

            const data = await response.json();
            const lista = Array.isArray(data) ? data : (data.results || []);
            setAsignaciones(lista);
        } catch (err) {
            setError('Error al cargar tus proyectos.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchMisProyectos();
    }, [fetchMisProyectos]);

    // Helper para badges de estado
    const getEstadoBadge = (estado) => {
        const estados = {
            'RECIBIDO': { bg: '#fff3cd', color: '#856404', label: 'RECIBIDO' },
            'ASIGNADO_REVISOR': { bg: '#d1ecf1', color: '#0c5460', label: 'ASIGNADO A REVISOR' },
            'EN_REVISION': { bg: '#e2d5f1', color: '#5a3d7a', label: 'EN REVISIÓN' },
            'APROBADO': { bg: '#d4edda', color: '#155724', label: 'APROBADO' },
            'RECHAZADO': { bg: '#f8d7da', color: '#721c24', label: 'RECHAZADO' },
            'OBSERVADO': { bg: '#ffe5cc', color: '#cc6600', label: 'OBSERVADO' }
        };

        const config = estados[estado] || { bg: '#e0e0e0', color: '#333', label: estado || 'SIN ESTADO' };

        return (
            <span style={{
                backgroundColor: config.bg,
                color: config.color,
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'inline-block',
                marginLeft: '10px'
            }}>
                {config.label}
            </span>
        );
    };

    const toggleProyectoCreado = async (proyectoId) => {
        if (proyectoCreadoExpandido === proyectoId) {
            setProyectoCreadoExpandido(null);
            setDocsCreados([]);
        } else {
            setProyectoCreadoExpandido(proyectoId);
            await cargarDocumentosCreados(proyectoId);
        }
    };

    const cargarDocumentosCreados = async (proyectoId) => {
        setError('');
        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/documentos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDocsCreados(data.documentos || []);
            } else if (response.status === 403) {
                setError('No tienes permiso para ver documentos de este proyecto');
            }
        } catch (err) {
            setError('Error al cargar documentos');
            console.error(err);
        }
    };

    const toggleProyecto = async (proyectoId) => {
        if (proyectoExpandido === proyectoId) {
            setProyectoExpandido(null);
            setDocumentos([]);
        } else {
            setProyectoExpandido(proyectoId);
            await cargarDocumentos(proyectoId);
        }
    };

    const cargarDocumentos = async (proyectoId) => {
        setError('');
        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/documentos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDocumentos(data.documentos || []);
            } else if (response.status === 403) {
                setError('No tienes permiso para ver documentos de este proyecto');
            }
        } catch (err) {
            setError('Error al cargar documentos');
            console.error(err);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                setError('El archivo es muy grande. Máximo 50MB');
                setArchivo(null);
                e.target.value = '';
                return;
            }
            setArchivo(file);
            setError('');
        }
    };

    const subirDocumento = async (e, proyectoId, esCreado = false) => {
        e.preventDefault();

        if (!archivo || !proyectoId) {
            setError('Selecciona un archivo');
            return;
        }

        setUploadLoading(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('archivo', archivo);
            formData.append('descripcion', descripcion);

            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/documentos/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess('Documento subido exitosamente');
                setArchivo(null);
                setDescripcion('');
                const inputId = esCreado ? 'fileInputCreados' : 'fileInputMisProyectos';
                const inputElement = document.getElementById(inputId);
                if (inputElement) inputElement.value = '';

                if (esCreado) {
                    cargarDocumentosCreados(proyectoId);
                } else {
                    cargarDocumentos(proyectoId);
                }
            } else {
                setError(data.detail || 'Error al subir documento');
            }
        } catch (err) {
            setError('Error al subir documento: ' + err.message);
        } finally {
            setUploadLoading(false);
        }
    };

    const descargarDocumento = async (documentoId) => {
        try {
            const response = await apiFetch(`${API_URL}/documentos/${documentoId}/download/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                window.open(data.download_link, '_blank');
            } else {
                setError('Error al generar link de descarga');
            }
        } catch (err) {
            setError('Error: ' + err.message);
        }
    };

    const compartirDocumento = async (documentoId) => {
        try {
            const response = await apiFetch(`${API_URL}/documentos/${documentoId}/share/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                navigator.clipboard.writeText(data.shared_link);
                setSuccess('Link copiado al portapapeles');
            } else {
                setError('Error al generar link compartido');
            }
        } catch (err) {
            setError('Error: ' + err.message);
        }
    };

    const eliminarDocumento = async (documentoId, esCreado = false) => {
        if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

        try {
            const response = await apiFetch(`${API_URL}/documentos/${documentoId}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess('Documento eliminado exitosamente');
                if (esCreado && proyectoCreadoExpandido) {
                    cargarDocumentosCreados(proyectoCreadoExpandido);
                } else if (proyectoExpandido) {
                    cargarDocumentos(proyectoExpandido);
                }
            } else {
                const data = await response.json();
                setError(data.detail || 'Error al eliminar documento');
            }
        } catch (err) {
            setError('Error: ' + err.message);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    if (loading) return <p>Cargando proyectos...</p>;
    if (error && proyectosCreados.length === 0 && asignaciones.length === 0) return <p className="error">{error}</p>;

    // Filtrar proyectos rechazados si el checkbox está activado
    const proyectosCreadosFiltrados = ocultarRechazados
        ? proyectosCreados.filter(p => p.status !== 'RECHAZADO')
        : proyectosCreados;

    const asignacionesFiltradas = ocultarRechazados
        ? asignaciones.filter(a => a.proyecto.status !== 'RECHAZADO')
        : asignaciones;

    return (
        <div className="section mis-proyectos">
            {error && <div className="alert alert--error">{error}</div>}
            {success && <div className="alert alert--success">{success}</div>}

            {/* Checkbox para ocultar rechazados */}
            <div className="filter-box">
                <label className="filter-label">
                    <input type="checkbox" checked={ocultarRechazados} onChange={(e) => setOcultarRechazados(e.target.checked)} className="filter-checkbox" />
                    <span className="filter-text">Ocultar proyectos rechazados</span>
                </label>
            </div>

            {/* SECCIÓN 1: Proyectos Creados por Mí */}
            <div style={{ marginBottom: '40px' }}>
                <h2>Proyectos Creados por Mí</h2>
                <p>Proyectos que has creado. Puedes subir documentos y ver su estado.</p>

                {proyectosCreadosFiltrados.length === 0 ? (
                    <p style={{ color: '#999', fontStyle: 'italic' }}>
                        {ocultarRechazados && proyectosCreados.length > 0
                            ? 'Todos tus proyectos están rechazados. Desmarca el filtro para verlos.'
                            : 'No has creado ningún proyecto aún.'}
                    </p>
                ) : (
                    <div>
                        {proyectosCreadosFiltrados.map((proyecto) => {
                            const puedeEditar = proyecto.puede_editar;

                            return (
                                <div key={proyecto.id} className={`project-card ${proyectoCreadoExpandido === proyecto.id ? 'is-expanded' : ''}`}>
                                    {/* Cabecera del proyecto creado */}
                                    <div onClick={() => toggleProyectoCreado(proyecto.id)} className={`project-header ${proyectoCreadoExpandido === proyecto.id ? 'header--expanded' : ''}`}>
                                        <div className="project-header-inner">
                                            <div className="project-main">
                                                <h3 className="project-title">{proyectoCreadoExpandido === proyecto.id ? '▼' : '▶'} {proyecto.nombre_proy || 'Proyecto'} {getEstadoBadge(proyecto.status)}</h3>
                                                <p className="project-sub">{proyecto.propietario || 'N/A'} | {proyecto.zona || 'N/A'}</p>
                                                <p className="project-meta">Creado: {new Date(proyecto.created_at).toLocaleDateString()} | Documentos: <strong>{proyecto.total_documentos || 0}</strong>
                                                    {!puedeEditar && (<span className="read-only"> 🔒 Solo lectura</span>)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contenido expandido - Documentos creados */}
                                    {proyectoCreadoExpandido === proyecto.id && (
                                        <div className="project-content">
                                            <h4>📄 Documentos del Proyecto</h4>

                                            {/* Formulario para subir - solo si puede editar */}
                                            {puedeEditar ? (
                                                <form onSubmit={(e) => subirDocumento(e, proyecto.id, true)} className="upload-form">
                                                    <h5>Subir Nuevo Documento</h5>
                                                    <div className="form-group">
                                                        <label>Archivo (máx. 50MB):</label>
                                                        <input id="fileInputCreados" type="file" onChange={handleFileChange} disabled={uploadLoading} className="file-input" />
                                                        {archivo && <span className="success-text">✓ {archivo.name} ({formatBytes(archivo.size)})</span>}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Descripción (opcional):</label>
                                                        <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Describe el documento..." disabled={uploadLoading} rows="3" className="form-textarea" />
                                                    </div>
                                                    <button type="submit" disabled={uploadLoading || !archivo} className={`btn ${archivo ? 'btn--upload' : 'btn--disabled'}`}>{uploadLoading ? 'Subiendo...' : '⬆️ Subir Documento'}</button>
                                                </form>
                                            ) : (
                                                <div className="read-only-box">🔒 <strong>Modo solo lectura:</strong> Este proyecto ya no está en estado RECIBIDO. Solo puedes visualizar y descargar documentos.</div>
                                            )}

                                            {/* Lista de documentos */}
                                            {docsCreados.length === 0 ? (
                                                <p className="muted italic">No hay documentos en este proyecto</p>
                                            ) : (
                                                <div>
                                                    <h5>Documentos ({docsCreados.length})</h5>
                                                    {docsCreados.map((doc) => (
                                                        <div key={doc.id} className="doc-card">
                                                            <div>
                                                                <h4 className="doc-title">📄 {doc.nombre_archivo}</h4>
                                                                {doc.descripcion && <p className="doc-desc">{doc.descripcion}</p>}
                                                                <p className="doc-meta">📊 {formatBytes(doc.tamaño_bytes)} | 📁 {doc.tipo_mime} | 📅 {new Date(doc.fecha_subida).toLocaleString('es-BO')}</p>
                                                                {doc.subido_por_detalle && <p className="doc-meta">Subido por: {doc.subido_por_detalle.nombre} {doc.subido_por_detalle.apellidos}</p>}
                                                            </div>
                                                            <div className="doc-actions">
                                                                <button onClick={() => descargarDocumento(doc.id)} className="btn btn--primary">⬇️ Descargar</button>
                                                                <button onClick={() => compartirDocumento(doc.id)} className="btn btn--secondary">🔗 Compartir</button>
                                                                {puedeEditar && <button onClick={() => eliminarDocumento(doc.id, true)} className="btn btn--danger">🗑️ Eliminar</button>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* SECCIÓN 2: Proyectos Asignados a Mí */}
            <div style={{ borderTop: '3px solid #ddd', paddingTop: '30px' }}>
                <h2>Proyectos Asignados a Mí</h2>
                <p>Proyectos donde has sido asignado como revisor o colaborador.</p>

                {asignacionesFiltradas.length === 0 ? (
                    <p style={{ color: '#999', fontStyle: 'italic' }}>
                        {ocultarRechazados && asignaciones.length > 0
                            ? 'Todos tus proyectos asignados están rechazados. Desmarca el filtro para verlos.'
                            : 'No tienes proyectos asignados.'}
                    </p>
                ) : (
                    <div>
                        {asignacionesFiltradas.map((asignacion) => {
                            const proyecto = asignacion.proyecto_detalle || {};
                            const proyectoId = proyecto.id || asignacion.proyecto;

                            return (
                                        <div key={asignacion.id} className={`project-card ${proyectoExpandido === proyectoId ? 'is-expanded' : ''}`}>
                                    {/* Cabecera del proyecto asignado */}
                                    <div onClick={() => toggleProyecto(proyectoId)} className={`project-header ${proyectoExpandido === proyectoId ? 'header--expanded' : ''}`}>
                                        <div className="project-header-inner">
                                            <div className="project-main">
                                                <h3 className="project-title">{proyectoExpandido === proyectoId ? '▼' : '▶'} {proyecto.nombre_proy || 'Proyecto'} {getEstadoBadge(proyecto.status)}</h3>
                                                <p className="project-sub">{proyecto.propietario || 'N/A'} | {proyecto.zona || 'N/A'}</p>
                                                <p className="project-meta">Asignado: {new Date(asignacion.fecha_asignacion).toLocaleDateString()} | Rol: <strong>{asignacion.staff_role || 'SOCIO'}</strong></p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contenido expandido - Documentos asignados */}
                                    {proyectoExpandido === proyectoId && (
                                        <div className="project-content">
                                            <h4>📄 Documentos del Proyecto</h4>

                                            <form onSubmit={(e) => subirDocumento(e, proyectoId, false)} className="upload-form">
                                                <h5>Subir Nuevo Documento</h5>
                                                <div className="form-group">
                                                    <label>Archivo (máx. 50MB):</label>
                                                    <input id="fileInputMisProyectos" type="file" onChange={handleFileChange} disabled={uploadLoading} className="file-input" />
                                                    {archivo && <span className="success-text">✓ {archivo.name} ({formatBytes(archivo.size)})</span>}
                                                </div>
                                                <div className="form-group">
                                                    <label>Descripción (opcional):</label>
                                                    <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Describe el documento..." disabled={uploadLoading} rows="3" className="form-textarea" />
                                                </div>
                                                <button type="submit" disabled={uploadLoading || !archivo} className={`btn ${archivo ? 'btn--upload' : 'btn--disabled'}`}>{uploadLoading ? 'Subiendo...' : '⬆️ Subir Documento'}</button>
                                            </form>

                                            {documentos.length === 0 ? (
                                                <p className="muted italic">No hay documentos en este proyecto</p>
                                            ) : (
                                                <div>
                                                    <h5>Documentos ({documentos.length})</h5>
                                                    {documentos.map((doc) => (
                                                        <div key={doc.id} className="doc-card">
                                                            <div>
                                                                <h4 className="doc-title">📄 {doc.nombre_archivo}</h4>
                                                                {doc.descripcion && <p className="doc-desc">{doc.descripcion}</p>}
                                                                <p className="doc-meta">📊 {formatBytes(doc.tamaño_bytes)} | 📁 {doc.tipo_mime} | 📅 {new Date(doc.fecha_subida).toLocaleString('es-BO')}</p>
                                                                {doc.subido_por_detalle && <p className="doc-meta">Subido por: {doc.subido_por_detalle.nombre} {doc.subido_por_detalle.apellidos}</p>}
                                                            </div>
                                                            <div className="doc-actions">
                                                                <button onClick={() => descargarDocumento(doc.id)} className="btn btn--primary">⬇️ Descargar</button>
                                                                <button onClick={() => compartirDocumento(doc.id)} className="btn btn--secondary">🔗 Compartir</button>
                                                                <button onClick={() => eliminarDocumento(doc.id, false)} className="btn btn--danger">🗑️ Eliminar</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MisProyectosConDocs;
